import Region from '../../../../../model/market_state/Region';
import type { PromotionCountryCell } from '../../../../../model/promotion/PromotionCountryOverview';
export { formatDay, formatDayYear } from './PromotionsPage.utilities';

const REGION_LABELS: Record<string, string> = {
	[Region.EUROPE]: 'Europe',
	[Region.UNITED_STATES]: 'United States',
	[Region.UNITED_KINGDOM]: 'United Kingdom',
	[Region.CANADA]: 'Canada',
	[Region.SWITZERLAND]: 'Switzerland',
	[Region.AUSTRALIA]: 'Australia',
	[Region.SINGAPORE]: 'Singapore',
	[Region.TURKEY]: 'Turkey',
	[Region.ISRAEL]: 'Israel',
	[Region.GREECE]: 'Greece',
};

export const regionLabel = (region: Region): string => REGION_LABELS[region] ?? region;

/** From this value on, a cell is a probable country rate: booking.com's minimum for a country rate is 5 %. */
export const COUNTRY_RATE_THRESHOLD = 5;

/** The text of a cell: `10%`, `10%*` (some rate plans only), `—` (no difference), `n/a`. */
export const countryCellText = (cell: PromotionCountryCell): string => {
	if (cell.discount == null) return 'n/a';
	if (cell.discount === 0) return '—';
	return `${cell.discount}%${cell.partial ? '*' : ''}`;
};

export const countryCellPlans = (cell: PromotionCountryCell): string =>
	cell.partial ? `${cell.plansDiscounted} of ${cell.plansMatched} rate plans` : 'all rate plans';

export const countryCellTooltip = (hotelName: string, cell: PromotionCountryCell, baseRegion: Region): string => {
	const country = regionLabel(cell.region);
	const base = regionLabel(baseRegion);
	if (cell.discount == null) return `${hotelName} — ${country}: too few compared nights`;
	if (cell.discount === 0) return `${hotelName} — ${country}: no consistent base-rate discount against ${base}`;
	return `${hotelName} — ${country}: visitors see a base rate ${cell.discount}% below ${base}, on ${countryCellPlans(cell)}`
		+ (cell.discount >= COUNTRY_RATE_THRESHOLD ? '. A probable country rate.' : '.');
};

/** The one-hour cooldown message, in the words of the Metaradar button (ROO2-65). */
export const countryRunCooldownMessage = (retryAfterSeconds: number): string => {
	if (retryAfterSeconds < 60) return 'Please wait less than a minute before running the countries again.';
	const minutes = Math.ceil(retryAfterSeconds / 60);
	if (minutes < 60) return `Please wait ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} before running the countries again.`;
	const hours = Math.ceil(minutes / 60);
	return `Please wait ${hours} ${hours === 1 ? 'hour' : 'hours'} before running the countries again.`;
};
